import "./cookie-consent.ts"; // GDPR banner/preferences

/* ============================================================
   NAV — scroll state
   ============================================================ */
const nav = document.getElementById("nav");
if (nav) {
  const onScroll = () => nav.classList.toggle("scrolled", window.scrollY > 24);
  onScroll();
  window.addEventListener("scroll", onScroll, { passive: true });
}

/* ============================================================
   MOBILE MENU
   ============================================================ */
const menuBtn = document.getElementById("menuBtn");
const mobileMenu = document.getElementById("mobileMenu");
if (menuBtn && mobileMenu) {
  menuBtn.addEventListener("click", () => {
    const open = mobileMenu.classList.toggle("open");
    menuBtn.setAttribute("aria-expanded", String(open));
  });
  for (const a of mobileMenu.querySelectorAll("a")) {
    a.addEventListener("click", () => mobileMenu.classList.remove("open"));
  }
}

/* ============================================================
   DOCUMENT TABS — switch between the four legal documents
   ============================================================ */
const DOCS = ["privacy", "terms", "gdpr", "residency"] as const;
type Doc = (typeof DOCS)[number];
const isDoc = (v: string): v is Doc => (DOCS as readonly string[]).includes(v);

const tabs = Array.from(document.querySelectorAll<HTMLElement>(".doc-tab"));
const onpage = document.getElementById("onpage");
const navOffset = 90;

/** Build the "On this page" rail for the active document and start scroll-spy. */
function buildOnPage(doc: Doc): void {
  if (!onpage) return;
  onpage.innerHTML = "";
  const panel = document.getElementById(doc);
  if (!panel) return;
  for (const sec of panel.querySelectorAll<HTMLElement>(".prose > section[id]")) {
    const h = sec.querySelector("h3");
    if (!h) continue;
    const label = (h.textContent ?? "").replace(/^\s*\d+\s*/, "").trim();
    const a = document.createElement("a");
    a.href = `#${sec.id}`;
    a.textContent = label;
    a.dataset.target = sec.id;
    a.addEventListener("click", (e) => {
      e.preventDefault();
      const top = sec.getBoundingClientRect().top + window.scrollY - navOffset;
      window.scrollTo({ top, behavior: "smooth" });
    });
    onpage.appendChild(a);
  }
  spy();
}

function activate(doc: string, push: boolean): void {
  const active: Doc = isDoc(doc) ? doc : "privacy";
  for (const t of tabs) t.classList.toggle("active", t.dataset.doc === active);
  for (const d of document.querySelectorAll<HTMLElement>(".doc")) d.classList.toggle("active", d.id === active);
  buildOnPage(active);
  if (push) history.replaceState(null, "", `#${active}`);
  window.scrollTo({ top: 0, behavior: "auto" });
}

for (const t of tabs) {
  t.addEventListener("click", () => activate(t.dataset.doc ?? "privacy", true));
}

/* Footer + inline links that jump to a document (data-doc-link="terms" …). */
for (const el of document.querySelectorAll<HTMLElement>("[data-doc-link]")) {
  el.addEventListener("click", (e) => {
    e.preventDefault();
    activate(el.getAttribute("data-doc-link") ?? "privacy", true);
  });
}

/* ============================================================
   SCROLL-SPY for the "On this page" rail
   ============================================================ */
function spy(): void {
  if (!onpage) return;
  const links = Array.from(onpage.querySelectorAll<HTMLAnchorElement>("a"));
  if (!links.length) return;
  let cur = links[0];
  let best = Infinity;
  for (const a of links) {
    const sec = a.dataset.target ? document.getElementById(a.dataset.target) : null;
    if (!sec) continue;
    const top = sec.getBoundingClientRect().top - navOffset - 6;
    const d = Math.abs(top);
    if (top <= 0 && d < best) {
      best = d;
      cur = a;
    }
  }
  for (const a of links) a.classList.toggle("active", a === cur);
}
window.addEventListener("scroll", spy, { passive: true });

/* ============================================================
   BACK TO TOP
   ============================================================ */
const totop = document.getElementById("totop");
if (totop) {
  const toggleTop = () => totop.classList.toggle("show", window.scrollY > 500);
  toggleTop();
  window.addEventListener("scroll", toggleTop, { passive: true });
  totop.addEventListener("click", () => window.scrollTo({ top: 0, behavior: "smooth" }));
}

/* ============================================================
   BOOT — open the document named in the URL hash (e.g. /legal#terms)
   ============================================================ */
const initial = (location.hash || "").replace("#", "");
activate(isDoc(initial) ? initial : "privacy", false);
